import React, { useState, useEffect } from 'react';
import { getAchievementDef } from './SkillTree/heroClasses';

/**
 * AchievementSequenceModal - Display new achievements in succession
 * Shows one achievement at a time with ability to go to next
 */
function AchievementSequenceModal({
  achievementIds = [],
  isOpen = false,
  onClose = () => {},
  onComplete = () => {}
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen && achievementIds.length > 0) {
      setCurrentIndex(0);
      setIsAnimating(true);
    }
  }, [isOpen, achievementIds.length]);

  if (!isOpen || achievementIds.length === 0) {
    return null;
  }

  const currentAchievementId = achievementIds[currentIndex];
  const achievement = getAchievementDef(currentAchievementId);
  const isLastAchievement = currentIndex === achievementIds.length - 1;

  if (!achievement) {
    return null;
  }

  const handleNext = () => {
    if (isLastAchievement) {
      setIsAnimating(false);
      setTimeout(() => {
        onComplete();
        onClose();
      }, 600);
    } else {
      setIsAnimating(false);
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setIsAnimating(true);
      }, 300);
    }
  };

  const handleSkipAll = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onComplete();
      onClose();
    }, 300);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      backdropFilter: 'blur(4px)',
      animation: isAnimating ? 'fadeIn 0.4s ease-out' : 'none'
    }}>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
        }

        .achievement-sequence-card {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          border: 2px solid #d4a574;
          border-radius: 16px;
          padding: 40px;
          max-width: 500px;
          text-align: center;
          box-shadow: 0 20px 60px rgba(212, 165, 116, 0.3),
                      0 0 40px rgba(212, 165, 116, 0.15);
          animation: slideIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
          position: relative;
          overflow: hidden;
        }

        .achievement-sequence-card::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(212, 165, 116, 0.1) 0%, transparent 70%);
          animation: float 6s ease-in-out infinite;
          pointer-events: none;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0);
          }
          50% {
            transform: translate(-20px, -20px);
          }
        }

        .achievement-sequence-icon {
          font-size: 80px;
          margin-bottom: 20px;
          animation: bounce 0.8s ease-in-out;
          display: inline-block;
          filter: drop-shadow(0 0 20px rgba(212, 165, 116, 0.4));
        }

        .achievement-sequence-title {
          font-family: 'Cinzel', serif;
          font-size: 32px;
          font-weight: 700;
          color: #f4d9a6;
          margin-bottom: 12px;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
          letter-spacing: 0.05em;
        }

        .achievement-sequence-description {
          font-family: 'Roboto', sans-serif;
          font-size: 16px;
          color: #c0c0c0;
          margin-bottom: 30px;
          line-height: 1.6;
          text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
        }

        .achievement-sequence-class {
          display: inline-block;
          font-size: 12px;
          padding: 6px 12px;
          background: rgba(212, 165, 116, 0.15);
          border: 1px solid rgba(212, 165, 116, 0.3);
          border-radius: 20px;
          color: #d4a574;
          margin-bottom: 25px;
          text-transform: capitalize;
          font-weight: 600;
          letter-spacing: 0.05em;
        }

        .achievement-sequence-progress {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 25px;
          font-size: 13px;
          color: #a0a0a0;
        }

        .achievement-sequence-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(212, 165, 116, 0.3);
          transition: all 0.3s ease;
        }

        .achievement-sequence-dot.active {
          background: #d4a574;
          box-shadow: 0 0 10px rgba(212, 165, 116, 0.6);
          transform: scale(1.3);
        }

        .achievement-sequence-buttons {
          display: flex;
          gap: 12px;
          margin-top: 30px;
          position: relative;
          z-index: 1;
        }

        .achievement-sequence-button {
          flex: 1;
          padding: 12px 20px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-family: 'Roboto', sans-serif;
        }

        .achievement-sequence-button-next {
          background: linear-gradient(135deg, #d4a574 0%, #f4d9a6 100%);
          color: #1a1a2e;
          flex: 2;
          box-shadow: 0 4px 15px rgba(212, 165, 116, 0.4);
        }

        .achievement-sequence-button-next:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(212, 165, 116, 0.6);
        }

        .achievement-sequence-button-next:active {
          transform: translateY(0);
        }

        .achievement-sequence-button-skip {
          background: rgba(212, 165, 116, 0.1);
          color: #d4a574;
          border: 1px solid rgba(212, 165, 116, 0.3);
          flex: 1;
        }

        .achievement-sequence-button-skip:hover {
          background: rgba(212, 165, 116, 0.2);
          border-color: rgba(212, 165, 116, 0.5);
        }
      `}</style>

      <div className="achievement-sequence-card">
        <div className="achievement-sequence-icon">
          {achievement.icon}
        </div>

        <div className="achievement-sequence-title">
          {achievement.name}
        </div>

        <div className="achievement-sequence-class">
          {achievement.class}
        </div>

        <div className="achievement-sequence-description">
          {achievement.desc}
        </div>

        <div className="achievement-sequence-progress">
          {achievementIds.map((_, idx) => (
            <div
              key={idx}
              className={`achievement-sequence-dot ${idx === currentIndex ? 'active' : ''}`}
            />
          ))}
          <span style={{ marginLeft: '12px' }}>
            {currentIndex + 1} / {achievementIds.length}
          </span>
        </div>

        <div className="achievement-sequence-buttons">
          <button
            className="achievement-sequence-button achievement-sequence-button-skip"
            onClick={handleSkipAll}
          >
            Skip All
          </button>
          <button
            className="achievement-sequence-button achievement-sequence-button-next"
            onClick={handleNext}
          >
            {isLastAchievement ? '✨ Finish' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AchievementSequenceModal;
